import { useBrandLocale } from '../../hooks/useBrandLocale';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { StayListingStatus } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import PriceCalendar from '../../components/PriceCalendar';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  DD,
  DescriptionList,
  DT,
  Field,
  Input,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Stack,
} from '../../components/ui';
import {
  useDeleteStayDraftMutation,
  usePublishStayMutation,
  useSnoozeStayMutation,
  useUnlistStayMutation,
} from '../../services/stayService/__generated__/queries.generated';
import { LISTINGS_PATH, listingEditPath } from './ListingWizardPage';
import {
  EMPTY_ADDRESS,
  StayAddressForm,
  addressProblem,
  toAddressInput,
  type StayAddress,
} from './StayAddressForm';
import { BasicsSection, StaySectionFields } from './StayFormSections';
import { StayPhotosEditor } from './StayPhotosEditor';
import { stayErrorMessage } from './stayErrors';
import {
  AMENITY_GROUPS,
  CANCELLATION_POLICY_LABELS,
  CHECK_IN_METHOD_LABELS,
  LISTING_STATUS_LABELS,
  PROPERTY_TYPE_LABELS,
  ROOM_TYPE_LABELS,
  SECTION_FIELDS,
  amenityLabel,
  isDraftStatus,
  listingSummary,
  type StayForm,
  type StaySectionId,
  type StayListing,
} from './stayForm';
import { useStayEditor } from './useStayEditor';

type EditableId = StaySectionId | 'location';

const Actions = styled.div`
  ${tw`flex flex-wrap justify-end gap-2 mt-4`}
`;

const Summary = styled.div`
  ${tw`text-sm`}
  color: var(--color-text-secondary);
  white-space: pre-line;
`;

const fmtMoney = (value?: string | number | null) =>
  value === null || value === undefined || value === '' ? 'Not set' : `$${value}`;

function summaryFor(section: EditableId, form: StayForm, listing: StayListing): ReactNode {
  switch (section) {
    case 'title':
      return form.name || 'No title yet';
    case 'description':
      return form.description || 'No description yet';
    case 'basics':
      return `${PROPERTY_TYPE_LABELS[form.propertyType]}, ${ROOM_TYPE_LABELS[form.roomType].title.toLowerCase()}. ${form.maxGuests} guests, ${form.bedrooms} bedrooms, ${form.beds} beds, ${form.bathrooms} baths`;
    case 'amenities': {
      const known = AMENITY_GROUPS.flatMap(group => group.items);
      const picked = known.filter(item => form.amenities.includes(item));
      return picked.length ? picked.map(amenityLabel).join(', ') : 'No amenities yet';
    }
    case 'location':
      return [listing.location?.address, listing.location?.city, listing.location?.state]
        .filter(Boolean)
        .join(', ');
    case 'booking':
      return `${form.instantBook ? 'Instant Book' : 'Requests only'}. ${form.minimumNights} to ${form.maximumNights} nights. Preparation: ${form.preparationDays === '0' ? 'none' : `${form.preparationDays} night(s)`}`;
    case 'houseRules':
      return [
        form.petsAllowed ? `Pets allowed (up to ${form.maxPets})` : 'No pets',
        form.smokingAllowed ? 'Smoking allowed' : 'No smoking',
        form.eventsAllowed ? 'Events allowed' : 'No events',
      ].join('. ');
    case 'price':
      return `${fmtMoney(form.weekdayPrice)} night, ${fmtMoney(form.weekendPrice || form.weekdayPrice)} weekend night. Cleaning ${fmtMoney(form.cleaningFee)}. Weekly ${form.weeklyDiscountPercent}% off, monthly ${form.monthlyDiscountPercent}% off`;
    case 'checkIn':
      return `Check-in ${form.checkInFrom}${form.checkInUntil ? ` to ${form.checkInUntil}` : ''}, checkout by ${form.checkOutBy}. ${form.checkInMethod ? CHECK_IN_METHOD_LABELS[form.checkInMethod] : 'Method not set'}`;
    case 'cancellation':
      return CANCELLATION_POLICY_LABELS[form.cancellationPolicy].title;
    default:
      return null;
  }
}

const SECTIONS: { id: EditableId; title: string }[] = [
  { id: 'title', title: 'Title' },
  { id: 'description', title: 'Description' },
  { id: 'basics', title: 'Property and guests' },
  { id: 'amenities', title: 'Amenities' },
  { id: 'location', title: 'Location' },
  { id: 'booking', title: 'Booking settings' },
  { id: 'houseRules', title: 'House rules' },
  { id: 'price', title: 'Pricing and discounts' },
  { id: 'checkIn', title: 'Check-in and checkout' },
  { id: 'cancellation', title: 'Cancellation policy' },
];

function SnoozeModal({
  open,
  onClose,
  onConfirm,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (until: Date) => void;
  loading: boolean;
}) {
  const [until, setUntil] = useState('');
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  return (
    <Modal isOpen={open} onClose={onClose} title="Snooze listing" modalWidth="420px">
      <Stack $gap={4}>
        <h3>Snooze your listing</h3>
        <Muted $size="sm">
          Guests cannot find or book your place until this date. It lists again on its own.
        </Muted>
        <Field label="List again on" htmlFor="stay-snooze-until">
          <Input
            id="stay-snooze-until"
            type="date"
            min={tomorrow}
            value={until}
            onChange={event => setUntil(event.target.value)}
          />
        </Field>
        <Button
          type="button"
          text="Snooze"
          isDisabled={!until}
          isLoading={loading}
          onClick={() => onConfirm(new Date(`${until}T00:00:00`))}
        />
      </Stack>
    </Modal>
  );
}

/** One place's settings, section by section (spec 016 W8). */
export default function ListingManagePage() {
  const brandCountry = useBrandLocale().country;
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const editor = useStayEditor(id);
  const { listing, form, errors, change, reset, save, saving } = editor;
  const [editing, setEditing] = useState<EditableId | null>(null);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const [address, setAddress] = useState<StayAddress>(EMPTY_ADDRESS);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [publishStay, { loading: publishing }] = usePublishStayMutation();
  const [unlistStay, { loading: unlisting }] = useUnlistStayMutation();
  const [snoozeStay, { loading: snoozing }] = useSnoozeStayMutation();
  const [deleteDraft, { loading: deleting }] = useDeleteStayDraftMutation();

  useEffect(() => {
    if (!listing?.location || editing === 'location') return;
    setAddress({
      street: listing.location.address?.split(',')[0] ?? '',
      unit: listing.location.address?.split(',').slice(1).join(',').trim() ?? '',
      city: listing.location.city ?? '',
      state: listing.location.state ?? '',
      zipCode: listing.location.zipCode ?? '',
      latitude: listing.latitude ?? null,
      longitude: listing.longitude ?? null,
    });
  }, [listing, editing]);

  if (editor.loading) {
    return (
      <PageContainer width="lg" canvas>
        <LoadingBlock label="Loading your listing" />
      </PageContainer>
    );
  }
  if (!listing || !id) {
    return (
      <PageContainer width="lg" canvas>
        <Alert tone="error" title="Listing not found">
          {editor.loadError
            ? stayErrorMessage(editor.loadError)
            : 'This listing could not be loaded.'}
        </Alert>
      </PageContainer>
    );
  }

  const status = listing.stayDetails?.listingStatus ?? StayListingStatus.Draft;
  const statusLabel = LISTING_STATUS_LABELS[status];

  const startEdit = (section: EditableId) => {
    reset();
    setSectionError(null);
    setEditing(section);
  };
  const cancelEdit = () => {
    reset();
    setSectionError(null);
    setEditing(null);
  };

  const saveSection = async () => {
    if (!editing) return;
    setSectionError(null);
    let result;
    if (editing === 'location') {
      const problem = addressProblem(address, brandCountry);
      if (problem) {
        setAddressError(problem);
        return;
      }
      result = await save([], { address: toAddressInput(address) });
    } else {
      result = await save(SECTION_FIELDS[editing]);
    }
    if (result.ok) {
      toast.success('Saved');
      setEditing(null);
    } else if (result.message) {
      setSectionError(result.message);
    }
  };

  const runAction = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      toast.success(done);
    } catch (error) {
      // STAY_NOT_PUBLISHABLE lists every missing item in its message.
      toast.error(stayErrorMessage(error));
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await deleteDraft({ variables: { id } });
      toast.success('Draft deleted');
      navigate(LISTINGS_PATH);
    } catch (error) {
      toast.error(stayErrorMessage(error));
    }
  };

  const headerActions = (
    <>
      {isDraftStatus(status) && (
        <Button
          type="button"
          theme="secondary"
          text="Continue setup"
          onClick={() => navigate(listingEditPath(id))}
        />
      )}
      {status !== StayListingStatus.Listed && (
        <Button
          type="button"
          text="Publish"
          isLoading={publishing}
          dataTestId="stay-manage-publish"
          onClick={() =>
            runAction(() => publishStay({ variables: { id } }), 'Your place is listed')
          }
        />
      )}
      {status === StayListingStatus.Listed && (
        <Button type="button" theme="secondary" text="Snooze" onClick={() => setSnoozeOpen(true)} />
      )}
      {(status === StayListingStatus.Listed || status === StayListingStatus.Snoozed) && (
        <Button
          type="button"
          theme="secondary"
          text="Unlist"
          isLoading={unlisting}
          dataTestId="stay-manage-unlist"
          onClick={() =>
            runAction(() => unlistStay({ variables: { id } }), 'Your place is unlisted')
          }
        />
      )}
      {isDraftStatus(status) && (
        <Button
          type="button"
          theme="danger"
          text="Delete draft"
          isLoading={deleting}
          onClick={onDelete}
        />
      )}
    </>
  );

  const renderEditor = (section: EditableId) => {
    if (section === 'location') {
      return (
        <StayAddressForm
          value={address}
          onChange={next => {
            setAddress(next);
            setAddressError(null);
          }}
          error={addressError}
        />
      );
    }
    if (section === 'basics') {
      return <BasicsSection form={form} errors={errors} onChange={change} withTypes />;
    }
    return <StaySectionFields section={section} form={form} errors={errors} onChange={change} />;
  };

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        backTo={{ to: LISTINGS_PATH, label: 'Listings' }}
        eyebrow="Hosting"
        title={listing.name || 'Untitled place'}
        description={
          <>
            <Badge tone={statusLabel.tone} dot data-testid="stay-manage-status">
              {statusLabel.label}
            </Badge>{' '}
            {listingSummary(listing)}
            {status === StayListingStatus.Snoozed && listing.stayDetails?.snoozedUntil
              ? `. Lists again on ${new Date(listing.stayDetails.snoozedUntil).toLocaleDateString()}`
              : ''}
          </>
        }
        actions={headerActions}
      />
      <Stack $gap={6}>
        <Card>
          <CardHeader title="Photos" description="The first photo is the cover." />
          <CardBody>
            <StayPhotosEditor listing={listing} />
          </CardBody>
        </Card>
        {SECTIONS.map(section => (
          <Card key={section.id} data-testid={`stay-section-${section.id}`}>
            <CardHeader
              title={section.title}
              actions={
                editing === section.id ? null : (
                  <Button
                    type="button"
                    theme="text"
                    size="sm"
                    text="Edit"
                    onClick={() => startEdit(section.id)}
                    dataTestId={`stay-edit-${section.id}`}
                  />
                )
              }
            />
            <CardBody>
              {editing === section.id ? (
                <>
                  {sectionError && (
                    <Alert tone="error" title="Not saved">
                      {sectionError}
                    </Alert>
                  )}
                  {renderEditor(section.id)}
                  <Actions>
                    <Button type="button" theme="secondary" text="Cancel" onClick={cancelEdit} />
                    <Button
                      type="button"
                      text="Save"
                      isLoading={saving}
                      onClick={saveSection}
                      dataTestId={`stay-save-${section.id}`}
                    />
                  </Actions>
                </>
              ) : (
                <Summary>{summaryFor(section.id, form, listing)}</Summary>
              )}
            </CardBody>
          </Card>
        ))}
        <Card>
          <CardHeader
            title="Calendar"
            description="Set a price for single nights or block nights. Friday and Saturday use the weekend price."
          />
          <CardBody>
            {listing.priceCalendar?.length ? (
              <PriceCalendar
                carId={listing.id}
                priceCalendar={listing.priceCalendar as any}
                sectionTitle="Nightly prices"
                modeLabel="daily"
                allowDynamicPricing={false}
                weekdayPrice={listing.weekdayPrice}
                weekendPrice={listing.weekendPrice}
                holidayPrice={listing.holidayPrice}
                timezone={listing.location?.timezone ?? undefined}
              />
            ) : (
              <Muted $size="sm">Set a nightly price first.</Muted>
            )}
          </CardBody>
        </Card>
        <Card $tone="muted">
          <CardBody>
            <DescriptionList>
              <DT>Listing ID</DT>
              <DD>{listing.id}</DD>
              <DT>Time zone</DT>
              <DD>{listing.location?.timezone ?? 'Unknown'}</DD>
            </DescriptionList>
          </CardBody>
        </Card>
      </Stack>
      <SnoozeModal
        open={snoozeOpen}
        loading={snoozing}
        onClose={() => setSnoozeOpen(false)}
        onConfirm={until =>
          runAction(async () => {
            await snoozeStay({ variables: { id, until: until.toISOString() } });
            setSnoozeOpen(false);
          }, 'Your place is snoozed')
        }
      />
    </PageContainer>
  );
}
